import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authClient } from '../../utils/amplifyClient';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import { UpdateEquipmentCategoryInput } from '../../API';
import { SingleUnit } from './equipmentTypes';
import {
    deleteSingleUnit as deleteSingleUnitMutation,
    updateEquipmentCategory as updateEquipmentCategoryMutation,
} from '../../graphql/mutations';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useUserContext } from '../../contexts/UserContext';
import { useFormsContext } from '../../contexts/FormsContext';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { deleteImageFromS3, resizeFile } from '../../utils/helperFunctions';
import { S3_IMAGE_PATH } from '../../utils/constants';
import { SingleUnitPMTab } from '../pm/SingleUnitPMTab';
import { SingleUnitCostTab } from '../cost-tracking/SingleUnitCostTab';
import { TiresTab } from '../SingleUnitTiresTab';
import '../../css/SingleUnitContent.css';
import { EquipmentOverviewTab } from './EquipmentOverviewTab';
import { EquipmentInspectionsTab } from './EquipmentInspectionsTab';
import { EquipmentDailyServiceTab } from './EquipmentDailyServiceTab';
import { EquipmentLockedTabContent } from './EquipmentLockedTabContent';
import { EquipmentUnsavedExitModal } from './EquipmentFormShared';
import { UnsavedChangesBar } from './UnsavedChangesBar';
import {
    getEquipmentCategoryEditPath,
    ADD_DAILY_SERVICE_FORM_OPTION_LABEL,
    ADD_INSPECTION_FORM_OPTION_LABEL,
    NEW_UNIT_PRIORITY_FIELDS,
    SINGLE_UNIT_EDIT_FORM_EXCLUDED_KEYS,
} from './equipmentConstants';
import {
    appendEquipmentEditHistory,
    buildEquipmentEditChanges,
} from './equipmentEditHistory';
import {
    mergeUnitLocalFields,
    writeUnitLocalFields,
    type UnitLocalFieldSnapshot,
} from './equipmentUnitEngineFieldStorage';
import {
    resolveUnitCategoryFormId,
    resolveUnitCategoryName,
    normalizeEquipmentUnitLocationId,
} from './equipmentFieldUtils';
import {
    fetchLatestDailyServiceReportForUnit,
    fetchLatestInspectionReportForUnit,
    raisedCurrentUM,
    resolveDailyServiceReportDisplayDate,
    resolveInspectionReportDisplayDate,
    resolveUMCounterFromDailyServiceReport,
    resolveUMCounterFromInspectionReport,
} from '../../utils/reportUtils';
import { EquipmentTabType, FieldValidationConfig, UnitRecord } from './equipmentTypes';
import { useEquipmentUnitFieldConfigs } from './useEquipmentUnitFieldConfigs';
import { useEquipmentCategoryAutosuggest } from './useEquipmentCategoryAutosuggest';
import { getUnitNameValidationMessage, validateEngineSerialNumber, validateLicenseNumber, validateSerialNumber, validateUnitName, UnitNameValidationError } from './equipmentUnitNameUtils';
import { normalizeUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { ensureCompanyUnitsOfMeasureSeeded } from '../../utils/companyUnitOfMeasureCatalog';
import { normalizeStoredYearValue } from '../../utils/dateInputUtils';
import { appendStampedNote, getUnitNotes, upsertUnitNotes } from '../../utils/unitNotes';
import { PageHeader, BackButton, Btn, Card, TabNav } from '@ui';
import { IconPencil } from '@icons';
import { FormBuilderOverlay } from '../pages-forms';
import { hasPermission, Permission } from '../../utils/permissions';
import { FORM_TYPES } from '../../utils/constants';

const SINGLE_UNIT_EDIT_FORM_EXCLUDED = new Set<string>(SINGLE_UNIT_EDIT_FORM_EXCLUDED_KEYS);

/** Not on deployed AppSync yet — persisted locally until Gen2 deploy. */
const LOCAL_ONLY_UNIT_FIELD_KEYS: Array<keyof UnitLocalFieldSnapshot> = [
    'assignedTo',
    'engineManufacturer',
    'engineModelNumber',
    'engineSerialNumber',
    'utilizationAdjustmentDays',
    'editHistory',
    'replacedUMCounterHistory',
];

const readLocalOnlyFieldsFromForm = (formData: Partial<SingleUnit>, unit: UnitRecord): UnitLocalFieldSnapshot => ({
    assignedTo:
        (formData as UnitRecord).assignedTo !== undefined
            ? ((formData as UnitRecord).assignedTo as string | null)
            : ((unit as UnitRecord).assignedTo ?? null),
    engineManufacturer:
        (formData as UnitRecord).engineManufacturer !== undefined
            ? ((formData as UnitRecord).engineManufacturer as string | null)
            : ((unit as UnitRecord).engineManufacturer ?? null),
    engineModelNumber:
        (formData as UnitRecord).engineModelNumber !== undefined
            ? ((formData as UnitRecord).engineModelNumber as string | null)
            : ((unit as UnitRecord).engineModelNumber ?? null),
    engineSerialNumber:
        (formData as UnitRecord).engineSerialNumber !== undefined
            ? ((formData as UnitRecord).engineSerialNumber as string | null)
            : ((unit as UnitRecord).engineSerialNumber ?? null),
    utilizationAdjustmentDays:
        (formData as UnitRecord).utilizationAdjustmentDays !== undefined
            ? ((formData as UnitRecord).utilizationAdjustmentDays as number | null)
            : ((unit as UnitRecord).utilizationAdjustmentDays ?? null),
    editHistory:
        (formData as UnitRecord).editHistory !== undefined
            ? ((formData as UnitRecord).editHistory as string | null)
            : ((unit as UnitRecord).editHistory ?? null),
    replacedUMCounterHistory:
        (formData as UnitRecord).replacedUMCounterHistory !== undefined
            ? ((formData as UnitRecord).replacedUMCounterHistory as string | null)
            : ((unit as UnitRecord).replacedUMCounterHistory ?? null),
});

const isLocalOnlyUnitField = (key: string): key is keyof UnitLocalFieldSnapshot =>
    LOCAL_ONLY_UNIT_FIELD_KEYS.includes(key as keyof UnitLocalFieldSnapshot);

export const EquipmentUnitDetailContent = () => {
    const navigate = useNavigate();
    const { unitId } = useParams<{ unitId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [unit, setUnit] = useState<SingleUnit | null>(null);
    const [resolvedUmCounter, setResolvedUmCounter] = useState<number | null>(null);
    const [resolvedInspectionSummary, setResolvedInspectionSummary] = useState<{
        lastInspectionDate: string | null;
        lastInspectionUMCounter: number | null;
        lastDailyServiceDate: string | null;
        lastDailyServiceUMCounter: number | null;
    } | null>(null);
    const [unitImageUrl, setUnitImageUrl] = useState('');
    const {
        companyUnits,
        companyGroupedEquipmentData,
        loading,
        updateUnitInDatabase,
        updateUnit,
        refreshEquipment,
        updateCategory,
    } = useEquipmentContext();
    const { company, companyOwner, companyUser, businessAdmin } = useUserContext();
    const { companyForms } = useFormsContext();
    const { companyLocations } = useRegionAndLocationContext();
    const { registerGuard, tryGoBack, confirmPendingNavigation, cancelPendingNavigation } = useNavigationGuard();
    const [activeTab, setActiveTab] = useState<EquipmentTabType>('overview');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<SingleUnit>>({});
    const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [imageUploadError, setImageUploadError] = useState<string | null>(null);
    const [isNewUnit, setIsNewUnit] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [unitNameError, setUnitNameError] = useState<UnitNameValidationError | null>(null);
    const [serialNumberError, setSerialNumberError] = useState<UnitNameValidationError | null>(null);
    const [engineSerialNumberError, setEngineSerialNumberError] = useState<UnitNameValidationError | null>(null);
    const [licenseNumberError, setLicenseNumberError] = useState<UnitNameValidationError | null>(null);
    const [showUnsavedExitModal, setShowUnsavedExitModal] = useState(false);
    const [showCancelNewUnitModal, setShowCancelNewUnitModal] = useState(false);
    const [editInspectionFormId, setEditInspectionFormId] = useState('');
    const [showInspectionFormOverlay, setShowInspectionFormOverlay] = useState(false);
    const [pendingInspectionFormName, setPendingInspectionFormName] = useState('');
    const [showDailyServiceFormOverlay, setShowDailyServiceFormOverlay] = useState(false);
    const [pendingDailyServiceFormName, setPendingDailyServiceFormName] = useState('');
    const formInitializedForUnitRef = useRef<string | null>(null);
    const unsavedExitIntentRef = useRef<'navigation' | 'closeEdit'>('navigation');
    const inspectionReportSyncAttemptedRef = useRef<string | null>(null);

    const canManageForms = hasPermission(companyOwner, companyUser, Permission.MANAGE_FORMS, businessAdmin);

    const formsList = useMemo(() => [...companyForms].sort((a, b) => a.name.localeCompare(b.name)), [companyForms]);

    const {
        sectionConfigs,
        fieldConfigs,
        editUnitFieldConfigs,
        companyId: fieldCompanyId,
        reloadServiceCenters,
    } = useEquipmentUnitFieldConfigs(formsList);
    const categoryAutosuggest = useEquipmentCategoryAutosuggest({
        categoryId: unit?.categoryId ?? undefined,
        formData,
        unit,
    });
    const { unitOfMeasureExtraValues, ...autosuggestOptions } = categoryAutosuggest;

    const unitForDisplay = useMemo((): UnitRecord | null => {
        if (!unit) return null;

        const merged: UnitRecord = { ...unit };

        if (unit.currentUMCounterValue == null && resolvedUmCounter != null) {
            merged.currentUMCounterValue = resolvedUmCounter;
        }

        const reportDate = resolvedInspectionSummary?.lastInspectionDate;
        if (reportDate) {
            merged.lastInspectionDate = reportDate;
        }

        const reportUm = resolvedInspectionSummary?.lastInspectionUMCounter;
        if (reportUm != null && Number.isFinite(reportUm)) {
            merged.lastInspectionUMCounter = reportUm;
        }

        const dailyDate = resolvedInspectionSummary?.lastDailyServiceDate;
        if (dailyDate) {
            merged.lastDailyServiceDate = dailyDate;
        }

        const dailyUm = resolvedInspectionSummary?.lastDailyServiceUMCounter;
        if (dailyUm != null && Number.isFinite(dailyUm)) {
            merged.lastDailyServiceUMCounter = dailyUm;
        }

        return merged;
    }, [unit, resolvedUmCounter, resolvedInspectionSummary]);

    useEffect(() => {
        if (searchParams.get('edit') === 'true') setIsEditing(true);
        if (searchParams.get('new') === 'true') setIsNewUnit(true);
        // Legacy Usage tab deep-link → Cost/Expense (Usage is embedded there).
        if (searchParams.get('tab') === 'usage') {
            setActiveTab(
                company?.costTrackingModuleEnrollment ? 'cost-expense' : 'overview',
            );
            const next = new URLSearchParams(searchParams);
            next.delete('tab');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, company?.costTrackingModuleEnrollment, setSearchParams]);

    useEffect(() => {
        if (!isEditing) {
            formInitializedForUnitRef.current = null;
            setEditInspectionFormId('');
            setUnitNameError(null);
            setSerialNumberError(null);
            setEngineSerialNumberError(null);
            setLicenseNumberError(null);
            return;
        }
        if (!unit?.id || unit.id !== unitId) return;
        if (formInitializedForUnitRef.current === unitId) return;

        const initialData: Record<string, any> = {};
        Object.values(fieldConfigs)
            .flat()
            .forEach((field) => {
                if (field.persisted !== false) {
                    const rawValue = (unit as UnitRecord)[field.key];
                    initialData[field.key] =
                        field.key === 'manufactureDate'
                            ? normalizeStoredYearValue(rawValue)
                            : field.key === 'locationId'
                              ? normalizeEquipmentUnitLocationId(
                                    rawValue as string | null | undefined,
                                    companyLocations,
                                )
                              : field.key === 'serviceCenterId'
                                ? String(rawValue ?? '')
                                : rawValue;
                }
            });
        setFormData(initialData as Partial<SingleUnit>);
        setEditInspectionFormId(
            resolveUnitCategoryFormId(unit, companyGroupedEquipmentData) || unit.category?.formId || '',
        );
        formInitializedForUnitRef.current = unitId;
    }, [isEditing, unitId, unit, fieldConfigs, companyLocations]);

    const validateUnitNameForEdit = useCallback(
        (value: string) => validateUnitName(value, companyUnits, companyGroupedEquipmentData, unit?.id),
        [companyUnits, companyGroupedEquipmentData, unit?.id],
    );

    const validateSerialNumberForEdit = useCallback(
        (value: string) => validateSerialNumber(value, companyUnits, companyGroupedEquipmentData, unit?.id),
        [companyUnits, companyGroupedEquipmentData, unit?.id],
    );

    const validateLicenseNumberForEdit = useCallback(
        (value: string) => validateLicenseNumber(value, companyUnits, companyGroupedEquipmentData, unit?.id),
        [companyUnits, companyGroupedEquipmentData, unit?.id],
    );

    const validateEngineSerialNumberForEdit = useCallback(
        (value: string) => validateEngineSerialNumber(value, companyUnits, companyGroupedEquipmentData, unit?.id),
        [companyUnits, companyGroupedEquipmentData, unit?.id],
    );

    const unitNameFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: unitNameError,
            validate: validateUnitNameForEdit,
            blockFocusUntilValid: true,
            onValidationError: setUnitNameError,
        }),
        [unitNameError, validateUnitNameForEdit],
    );

    const serialNumberFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: serialNumberError,
            validate: validateSerialNumberForEdit,
            blockFocusUntilValid: true,
            onValidationError: setSerialNumberError,
        }),
        [serialNumberError, validateSerialNumberForEdit],
    );

    const licenseNumberFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: licenseNumberError,
            validate: validateLicenseNumberForEdit,
            blockFocusUntilValid: true,
            onValidationError: setLicenseNumberError,
        }),
        [licenseNumberError, validateLicenseNumberForEdit],
    );

    const engineSerialNumberFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: engineSerialNumberError,
            validate: validateEngineSerialNumberForEdit,
            blockFocusUntilValid: true,
            onValidationError: setEngineSerialNumberError,
        }),
        [engineSerialNumberError, validateEngineSerialNumberForEdit],
    );

    const fieldValidations = useMemo(
        () =>
            isEditing
                ? {
                      name: unitNameFieldValidation,
                      serialNumber: serialNumberFieldValidation,
                      licenseNumber: licenseNumberFieldValidation,
                      engineSerialNumber: engineSerialNumberFieldValidation,
                  }
                : undefined,
        [
            isEditing,
            unitNameFieldValidation,
            serialNumberFieldValidation,
            licenseNumberFieldValidation,
            engineSerialNumberFieldValidation,
        ],
    );

    const isDirty = useMemo(() => {
        if (!isEditing || !unit) return false;

        const inspectionFormDirty =
            (editInspectionFormId || '') !== resolveUnitCategoryFormId(unit, companyGroupedEquipmentData);
        const formDirty = Object.keys(formData).some((key) => {
            if (SINGLE_UNIT_EDIT_FORM_EXCLUDED.has(key)) {
                return false;
            }
            const original = (unit as UnitRecord)[key] ?? '';
            const current = (formData as Record<string, any>)[key] ?? '';
            return String(original) !== String(current);
        });

        return inspectionFormDirty || formDirty;
    }, [formData, unit, isEditing, editInspectionFormId]);

    useEffect(() => {
        registerGuard(
            isDirty
                ? {
                      when: true,
                      onBlocked: () => {
                          unsavedExitIntentRef.current = 'navigation';
                          setShowUnsavedExitModal(true);
                      },
                  }
                : null,
        );
        return () => registerGuard(null);
    }, [isDirty, registerGuard]);

    const handleEnterEditMode = () => {
        setIsEditing(true);
        // Replace so Cancel → Back does not re-enter edit via history (`?edit=true`).
        setSearchParams({ edit: 'true' }, { replace: true });
    };

    const handleDiscard = () => {
        if (isNewUnit) {
            setShowCancelNewUnitModal(true);
            return;
        }
        formInitializedForUnitRef.current = null;
        setIsEditing(false);
        setFormData({});
        setEditInspectionFormId('');
        setUnitNameError(null);
        setSerialNumberError(null);
        setEngineSerialNumberError(null);
        setLicenseNumberError(null);
        searchParams.delete('edit');
        setSearchParams(searchParams, { replace: true });
    };

    const handleBackClick = () => {
        if (isEditing) {
            if (isDirty) {
                unsavedExitIntentRef.current = 'closeEdit';
                setShowUnsavedExitModal(true);
                return;
            }
            handleDiscard();
            return;
        }

        tryGoBack('/equipment');
    };

    const handleCancelNewUnit = async () => {
        if (unit?.id) {
            try {
                await authClient.graphql({
                    query: deleteSingleUnitMutation,
                    variables: { input: { id: unit.id } },
                });
            } catch (error) {
                console.error('Error deleting placeholder unit:', error);
            }
        }
        setShowCancelNewUnitModal(false);
        navigate('/equipment');
    };

    const handleSave = async () => {
        if (!unit?.id || !isDirty) return;
        const currentName = (formData.name ?? unit.name ?? '').trim();
        const nameValidationError = validateUnitNameForEdit((formData.name as string) ?? unit.name ?? '');
        if (nameValidationError) {
            setUnitNameError(nameValidationError);
            setSaveError(getUnitNameValidationMessage(nameValidationError));
            return;
        }
        if (!currentName || currentName === 'New Unit') {
            setSaveError('Please enter a unit name before saving.');
            return;
        }

        const serialValidationError = validateSerialNumberForEdit(
            (formData.serialNumber as string) ?? unit.serialNumber ?? '',
        );
        if (serialValidationError) {
            setSerialNumberError(serialValidationError);
            setSaveError(getUnitNameValidationMessage(serialValidationError));
            return;
        }

        const licenseValidationError = validateLicenseNumberForEdit(
            (formData.licenseNumber as string) ?? unit.licenseNumber ?? '',
        );
        if (licenseValidationError) {
            setLicenseNumberError(licenseValidationError);
            setSaveError(getUnitNameValidationMessage(licenseValidationError));
            return;
        }

        const engineSerialValidationError = validateEngineSerialNumberForEdit(
            ((formData as UnitRecord).engineSerialNumber as string) ??
                ((unit as UnitRecord).engineSerialNumber as string) ??
                '',
        );
        if (engineSerialValidationError) {
            setEngineSerialNumberError(engineSerialValidationError);
            setSaveError(getUnitNameValidationMessage(engineSerialValidationError));
            return;
        }
        setSaveError(null);
        try {
            const inspectionFormDirty =
                (editInspectionFormId || '') !== resolveUnitCategoryFormId(unit, companyGroupedEquipmentData);
            if (inspectionFormDirty && unit.categoryId) {
                if (!editInspectionFormId) {
                    setSaveError('An inspection form is required before saving this change.');
                    return;
                }
                const updateInput: UpdateEquipmentCategoryInput = {
                    id: unit.categoryId,
                    formId: editInspectionFormId,
                };
                const response = (await authClient.graphql({
                    query: updateEquipmentCategoryMutation,
                    variables: { input: updateInput },
                })) as { data?: { updateEquipmentCategory?: Parameters<typeof updateCategory>[1] } };
                if (response.data?.updateEquipmentCategory) {
                    updateCategory(unit.categoryId, response.data.updateEquipmentCategory);
                }
            }

            let nextExtraUnitInfo: string | null | undefined;
            const formDirty = Object.keys(formData).some((key) => {
                if (SINGLE_UNIT_EDIT_FORM_EXCLUDED.has(key)) {
                    return false;
                }
                const original = (unit as UnitRecord)[key] ?? '';
                const current = (formData as Record<string, any>)[key] ?? '';
                return String(original) !== String(current);
            });

            const changedFields: Record<string, unknown> = {};
            for (const key of Object.keys(formData)) {
                if (SINGLE_UNIT_EDIT_FORM_EXCLUDED.has(key) || key === 'editHistory') {
                    continue;
                }
                const original = (unit as UnitRecord)[key] ?? '';
                const current = (formData as Record<string, any>)[key] ?? '';
                if (String(original) !== String(current)) {
                    changedFields[key] = (formData as Record<string, any>)[key];
                }
            }

            if (formDirty) {
                const saveData: Record<string, unknown> = {};
                for (const [key, value] of Object.entries(changedFields)) {
                    if (isLocalOnlyUnitField(key)) {
                        continue;
                    }
                    saveData[key] = value;
                }
                if (saveData.unitOfMeasure) {
                    saveData.unitOfMeasure = normalizeUnitOfMeasure(String(saveData.unitOfMeasure));
                    const umCompanyId =
                        (company?.id as string) ||
                        companyOwner?.companyId ||
                        (companyUser?.companyId as string) ||
                        '';
                    if (umCompanyId && saveData.unitOfMeasure) {
                        await ensureCompanyUnitsOfMeasureSeeded(umCompanyId, [String(saveData.unitOfMeasure)]);
                    }
                }
                if (saveData.manufactureDate) {
                    saveData.manufactureDate = normalizeStoredYearValue(String(saveData.manufactureDate));
                }
                if (Object.prototype.hasOwnProperty.call(saveData, 'locationId')) {
                    const normalizedLocationId = normalizeEquipmentUnitLocationId(
                        String(saveData.locationId ?? ''),
                        companyLocations,
                    );
                    saveData.locationId = normalizedLocationId || null;
                }
                const noteDraft = String((formData as Record<string, unknown>).noteDraft ?? '').trim();
                if (noteDraft) {
                    nextExtraUnitInfo = upsertUnitNotes(
                        unit.extraUnitInfo,
                        appendStampedNote(
                            getUnitNotes(unit.extraUnitInfo),
                            noteDraft,
                            companyOwner?.name || companyUser?.name || 'Unknown',
                        ),
                    );
                    saveData.extraUnitInfo = nextExtraUnitInfo;
                    delete saveData.noteDraft;
                    delete changedFields.noteDraft;
                }
                if (Object.keys(saveData).length > 0) {
                    await updateUnitInDatabase(unit.id, saveData as Partial<SingleUnit>);
                }
            }

            const editChanges = buildEquipmentEditChanges(unit as UnitRecord, changedFields);
            const editorName = companyOwner?.name || companyUser?.name || 'Unknown';
            const editorId = companyOwner?.id || companyUser?.id || '';
            const nextEditHistory = appendEquipmentEditHistory(
                (unit as UnitRecord).editHistory as string | null | undefined,
                {
                    editedBy: editorName,
                    editedByUserId: editorId,
                    editedAt: new Date().toISOString(),
                    changes: editChanges,
                },
            );

            if (inspectionFormDirty) {
                await refreshEquipment();
            }

            const localSnapshot = readLocalOnlyFieldsFromForm(formData, unit as UnitRecord);
            localSnapshot.editHistory = nextEditHistory;
            writeUnitLocalFields(unit.id, localSnapshot);
            setUnit((prev: SingleUnit | null) =>
                prev
                    ? {
                          ...prev,
                          ...localSnapshot,
                          ...(nextExtraUnitInfo !== undefined ? { extraUnitInfo: nextExtraUnitInfo } : {}),
                      }
                    : prev,
            );
            updateUnit(unit.id, {
                ...localSnapshot,
                ...(nextExtraUnitInfo !== undefined ? { extraUnitInfo: nextExtraUnitInfo } : {}),
            } as Partial<SingleUnit>);

            setIsEditing(false);
            setIsNewUnit(false);
            setFormData({});
            setEditInspectionFormId('');
            setUnitNameError(null);
            setSerialNumberError(null);
            setEngineSerialNumberError(null);
            setLicenseNumberError(null);
            formInitializedForUnitRef.current = null;
            searchParams.delete('edit');
            searchParams.delete('new');
            setSearchParams(searchParams, { replace: true });
        } catch (error) {
            console.error('Error updating unit:', error);
            const message =
                (error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
                (error instanceof Error ? error.message : null);
            setSaveError(message || 'Failed to save unit. Please try again.');
        }
    };

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || (company?.id as string) || '';

    const handleImageUpload = async (file: File) => {
        if (!unit?.id) {
            setImageUploadError('Unit is not loaded yet. Please try again.');
            return;
        }
        if (!companyId) {
            setImageUploadError('Company context is not available. Refresh the page and try again.');
            return;
        }

        setImageUploadError(null);
        setIsUploadingImage(true);
        try {
            if (unit.image) {
                try {
                    await deleteImageFromS3(unit.image);
                } catch (deleteError) {
                    console.warn('Could not delete previous unit image; continuing with upload.', deleteError);
                }
            }
            const resizedImage = await resizeFile(file);
            const imageFile = resizedImage as File;
            const safeUnitName = (unit.name || 'unit').replace(/[^\w.-]+/g, '-');
            const fileName = `${uuidv4()}-IMAGE-${safeUnitName}-${imageFile.name}`;
            const savedImage = await uploadData({
                path: S3_IMAGE_PATH.fullEquipmentImagePath(companyId, fileName),
                data: imageFile,
            }).result;

            if (!savedImage.path) {
                throw new Error('Upload finished but no storage path was returned.');
            }

            await updateUnitInDatabase(unit.id, { image: savedImage.path });
            setUnit((prev: SingleUnit | null) => (prev ? { ...prev, image: savedImage.path } : prev));
            const response = await getUrl({ path: savedImage.path });
            setUnitImageUrl(response.url.href || '');
        } catch (error) {
            console.error('Error uploading image:', error);
            const message =
                (error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
                (error instanceof Error ? error.message : null);
            setImageUploadError(message || 'Failed to upload photo. Please try again.');
        } finally {
            setIsUploadingImage(false);
        }
    };

    const fetchImageUrl = async (imagePath: string) => {
        try {
            const response = await getUrl({ path: imagePath });
            setUnitImageUrl(response.url.href || '');
        } catch (error) {
            console.error('Error getting image URL:', error);
            setUnitImageUrl('');
        }
    };

    useEffect(() => {
        inspectionReportSyncAttemptedRef.current = null;
        setResolvedInspectionSummary(null);
    }, [unitId]);

    useEffect(() => {
        if (!unitId || companyUnits.length === 0) return;

        const foundUnit = companyUnits.find((u) => u.id === unitId);
        const mergedUnit = foundUnit ? mergeUnitLocalFields(unitId, foundUnit as UnitRecord) : null;
        setUnit(mergedUnit);
        if (foundUnit?.currentUMCounterValue != null) {
            setResolvedUmCounter(null);
        }
        if (foundUnit?.image) fetchImageUrl(foundUnit.image);
        else setUnitImageUrl('');
    }, [unitId, companyUnits]);

    /** Backfill unit summary fields from the latest inspection report when submit did not update SingleUnit. */
    useEffect(() => {
        if (!unit?.id) {
            return;
        }
        if (inspectionReportSyncAttemptedRef.current === unit.id) {
            return;
        }
        inspectionReportSyncAttemptedRef.current = unit.id;

        let isCancelled = false;

        (async () => {
            try {
                const [latest, latestDaily] = await Promise.all([
                    fetchLatestInspectionReportForUnit(unit.id),
                    fetchLatestDailyServiceReportForUnit(unit.id),
                ]);
                if (isCancelled) return;

                const reportDate = resolveInspectionReportDisplayDate(latest);
                const counter = resolveUMCounterFromInspectionReport(latest);
                const dailyDate = resolveDailyServiceReportDisplayDate(latestDaily);
                const dailyCounter = resolveUMCounterFromDailyServiceReport(latestDaily);

                if (!isCancelled) {
                    setResolvedInspectionSummary({
                        lastInspectionDate: reportDate ?? null,
                        lastInspectionUMCounter: counter != null && Number.isFinite(counter) ? counter : null,
                        lastDailyServiceDate: dailyDate ?? null,
                        lastDailyServiceUMCounter:
                            dailyCounter != null && Number.isFinite(dailyCounter) ? dailyCounter : null,
                    });
                }

                const updates: Record<string, string | number> = {};
                if (reportDate && (!unit.lastInspectionDate || reportDate > unit.lastInspectionDate)) {
                    updates.lastInspectionDate = reportDate;
                }
                if (dailyDate && (!unit.lastDailyServiceDate || dailyDate > unit.lastDailyServiceDate)) {
                    updates.lastDailyServiceDate = dailyDate;
                }
                const raised = raisedCurrentUM({
                    currentUM: unit.currentUMCounterValue,
                    unitUpdatedAt: unit.updatedAt,
                    readings: [
                        { value: counter, at: latest?.inspectionCompleted || latest?.createdAt },
                        {
                            value: dailyCounter,
                            at: latestDaily?.inspectionCompleted || latestDaily?.createdAt,
                        },
                    ],
                });
                if (raised != null) {
                    updates.currentUMCounterValue = raised;
                    setResolvedUmCounter(raised);
                } else if (unit.currentUMCounterValue == null && counter != null && Number.isFinite(counter)) {
                    updates.currentUMCounterValue = counter;
                    setResolvedUmCounter(counter);
                }

                if (Object.keys(updates).length === 0) {
                    return;
                }

                try {
                    await updateUnitInDatabase(unit.id, updates);
                    if (!isCancelled) {
                        setUnit((prev: SingleUnit | null) => (prev ? { ...prev, ...updates } : prev));
                        if (updates.currentUMCounterValue != null) {
                            setResolvedUmCounter(null);
                        }
                    }
                } catch (persistError) {
                    console.error('Failed to persist unit fields from latest inspection:', persistError);
                    Sentry.captureException(persistError);
                }
            } catch (error) {
                console.error('Failed to load latest inspection for unit sync:', error);
                Sentry.captureException(error);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [unit?.id, unit?.currentUMCounterValue, unit?.lastInspectionDate, updateUnitInDatabase]);

    const handleUnarchive = async () => {
        if (!unit?.id) return;
        try {
            const today = new Date().toISOString().split('T')[0];
            await updateUnitInDatabase(unit.id, {
                equipmentStatus: true,
                archivedDate: null,
                lastArchiveStatusChange: today,
            } as any);
            setShowUnarchiveModal(false);
        } catch (err) {
            console.error('Error unarchiving unit:', err);
        }
    };

    if (loading) return <div className="loading">Loading...</div>;
    if (!unit) return <div className="unit-error">Unit not found</div>;

    const isArchived = unit.equipmentStatus === false;

    const availableTabs: { id: EquipmentTabType; label: string; enabled: boolean; locked: boolean }[] = [
        { id: 'overview', label: 'Overview', enabled: true, locked: false },
        { id: 'inspections', label: 'Inspections', enabled: true, locked: isArchived },
        { id: 'daily-service', label: 'Daily Service', enabled: true, locked: isArchived },
        { id: 'pm', label: 'PM', enabled: true, locked: isArchived || !company?.pmModuleEnrollment },
        {
            id: 'tires',
            label: 'Tire Maintenance',
            enabled: true,
            locked: isArchived || !company?.tiresModuleEnrollment,
        },
        {
            id: 'cost-expense',
            label: 'Cost/Expense',
            enabled: true,
            locked: isArchived || !company?.costTrackingModuleEnrollment,
        },
    ];

    const activeTabConfig = availableTabs.find((t) => t.id === activeTab);

    const categoryBreadcrumbLabel = resolveUnitCategoryName(unit, companyGroupedEquipmentData);

    return (
        <div className={`page-container single-unit-page ${isArchived ? 'archived' : ''}`}>
            <PageHeader
                breadcrumbs={[
                    { label: 'Equipment', href: '/equipment' },
                    unit.categoryId && categoryBreadcrumbLabel !== '-'
                        ? {
                              label: categoryBreadcrumbLabel,
                              href: getEquipmentCategoryEditPath(unit.categoryId),
                          }
                        : { label: categoryBreadcrumbLabel },
                    { label: unit.name },
                ]}
            />

            {/* Archived Banner */}
            {isArchived && (
                <div className="archived-banner">
                    <span className="archived-banner-icon">🔒</span>
                    <span>
                        This unit is archived. All operations are locked — inspections, services, PM, tires, and cost
                        tracking are disabled.
                    </span>
                </div>
            )}

            <Card className="card-xl card-pad-0 unit-detail">
                {/* Unit Title Section */}
                <div className="unit-title-section">
                    <BackButton
                        fallbackTo="/equipment"
                        onClick={handleBackClick}
                        buttonText={isEditing ? 'Cancel' : 'Back'}
                        ariaLabel={isEditing ? 'Cancel editing and return to unit view' : 'Back to equipment list'}
                    />
                    <div className="unit-title-content">
                        <div
                            className="unit-image-wrapper"
                            onClick={() => !isArchived && imageInputRef.current?.click()}
                        >
                            {unitImageUrl ? (
                                <img
                                    src={unitImageUrl}
                                    alt={unit.name}
                                    className="unit-image"
                                    onError={(e) => {
                                        e.currentTarget.src = '/image-not-found.jpeg';
                                    }}
                                />
                            ) : (
                                <img src="/image-not-found.jpeg" alt={unit.name} className="unit-image" />
                            )}
                            {!isArchived && <div className="unit-image-overlay">{isUploadingImage ? '⏳' : '📷'}</div>}
                            {imageUploadError && !isEditing && (
                                <p className="unit-image-upload-error" role="alert">
                                    {imageUploadError}
                                </p>
                            )}
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(file);
                                    e.target.value = '';
                                }}
                            />
                        </div>
                        <div className="unit-title-info">
                            {/* <div className="breadcrumb">
                                Equipment &gt; {unit.category?.name || 'Category'} &gt; {unit.name}
                            </div> */}
                            <h2>{unit.name}</h2>
                            <p className="unit-subtitle">{unit.description || 'No description'}</p>
                        </div>
                        <div className="unit-actions">
                            {!isEditing && !isArchived && activeTab === 'overview' && (
                                <Btn
                                    variant="warning"
                                    buttonText="Edit"
                                    buttonIcon={<IconPencil />}
                                    buttonIconPosition="left"
                                    buttonOnClick={handleEnterEditMode}
                                    ariaLabel="Edit unit"
                                />
                            )}
                            {!isEditing && isArchived && (
                                <button
                                    className="action-btn edit-action"
                                    onClick={() => {
                                        const lastChange = (unit as { lastArchiveStatusChange?: string | null }).lastArchiveStatusChange;
                                        if (lastChange) {
                                            const daysSinceChange =
                                                (Date.now() - new Date(lastChange).getTime()) / (1000 * 60 * 60 * 24);
                                            if (daysSinceChange < 30) {
                                                const daysRemaining = Math.ceil(30 - daysSinceChange);
                                                alert(
                                                    `Archive status can only be changed once per month. Please wait ${daysRemaining} more day${daysRemaining > 1 ? 's' : ''}.`,
                                                );
                                                return;
                                            }
                                        }
                                        setShowUnarchiveModal(true);
                                    }}
                                >
                                    <span className="action-btn-icon">📂</span> Unarchive
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <TabNav
                    variant="tabs"
                    ariaLabel="Equipment unit tabs"
                    className="unit-detail-tabs"
                    tabButtonClasses="unit-tab"
                    tabs={availableTabs.map((tab) => ({
                        id: tab.id,
                        label: tab.locked ? (
                            <>
                                <span className="tab-lock-icon">🔒</span>
                                {tab.label}
                            </>
                        ) : (
                            tab.label
                        ),
                    }))}
                    activeTabId={activeTab}
                    onTabChange={(tabId) => setActiveTab(tabId as EquipmentTabType)}
                    flared={false}
                />
                {/* Unsaved Changes Bar */}
                {isEditing && (
                    <UnsavedChangesBar dirty={isDirty}>
                        <span className="unsaved-changes-text">
                            {isNewUnit
                                ? 'New unit — rename and fill in details to save'
                                : isDirty
                                  ? 'You have unsaved changes'
                                  : 'Editing mode'}
                        </span>
                        {saveError && <span className="unsaved-changes-error">{saveError}</span>}
                        {imageUploadError && <span className="unsaved-changes-error">{imageUploadError}</span>}
                        <div className="unsaved-changes-actions">
                            {isDirty ? (
                                <>
                                    <Btn
                                        variant="secondary"
                                        buttonText={isNewUnit ? 'Cancel' : 'Discard'}
                                        buttonOnClick={handleDiscard}
                                    />
                                    <Btn variant="ghost" buttonText="Save" buttonOnClick={handleSave} />
                                </>
                            ) : (
                                <Btn
                                    variant="secondary"
                                    buttonText={isNewUnit ? 'Close Create Mode' : 'Close Edit Mode'}
                                    buttonOnClick={handleDiscard}
                                />
                            )}
                        </div>
                    </UnsavedChangesBar>
                )}
                {/* Cancel New Unit Confirmation Modal */}
                {showCancelNewUnitModal && (
                    <div className="confirm-modal-overlay" onClick={() => setShowCancelNewUnitModal(false)}>
                        <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>Discard new unit?</h3>
                            <p>
                                This unit hasn't been named yet and will be deleted. Are you sure you want to go back?
                            </p>
                            <div className="confirm-modal-actions">
                                <Btn
                                    variant="secondary-outlined"
                                    buttonText="Keep Editing"
                                    buttonOnClick={() => setShowCancelNewUnitModal(false)}
                                />
                                <Btn variant="danger" buttonText="Delete & Go Back" buttonOnClick={handleCancelNewUnit} />
                            </div>
                        </div>
                    </div>
                )}
                {/* Tab Content */}
                <div className="tab-content">
                    {activeTabConfig?.locked ? (
                        <EquipmentLockedTabContent tabId={activeTab} />
                    ) : (
                        <>
                            {activeTab === 'overview' && unitForDisplay && (
                                <EquipmentOverviewTab
                                    unit={unitForDisplay}
                                    isEditing={isEditing}
                                    isNewUnit={isNewUnit}
                                    priorityFields={NEW_UNIT_PRIORITY_FIELDS}
                                    formData={formData}
                                    setFormData={setFormData}
                                    sectionConfigs={sectionConfigs}
                                    fieldConfigs={isEditing ? editUnitFieldConfigs : fieldConfigs}
                                    autosuggestOptions={autosuggestOptions}
                                    unitOfMeasureExtraValues={unitOfMeasureExtraValues}
                                    inspectionFormId={editInspectionFormId}
                                    onInspectionFormIdChange={setEditInspectionFormId}
                                    inspectionFormSelectVariant={canManageForms ? 'search-suggest' : 'select'}
                                    inspectionFormActionOption={
                                        canManageForms
                                            ? {
                                                  label: ADD_INSPECTION_FORM_OPTION_LABEL,
                                                  onClick: ({ searchText }) => {
                                                      setPendingInspectionFormName(searchText.trim());
                                                      setShowInspectionFormOverlay(true);
                                                  },
                                              }
                                            : undefined
                                    }
                                    dailyServiceFormSelectVariant={canManageForms ? 'search-suggest' : 'select'}
                                    dailyServiceFormActionOption={
                                        canManageForms
                                            ? {
                                                  label: ADD_DAILY_SERVICE_FORM_OPTION_LABEL,
                                                  onClick: ({ searchText }) => {
                                                      setPendingDailyServiceFormName(searchText.trim());
                                                      setShowDailyServiceFormOverlay(true);
                                                  },
                                              }
                                            : undefined
                                    }
                                    unitImageUrl={unitImageUrl}
                                    onImageSelect={handleImageUpload}
                                    isUploadingImage={isUploadingImage}
                                    imageUploadError={imageUploadError}
                                    fieldValidations={fieldValidations}
                                    companyId={fieldCompanyId}
                                    reloadServiceCenters={reloadServiceCenters}
                                />
                            )}
                            {activeTab === 'inspections' && <EquipmentInspectionsTab unit={unit} />}
                            {activeTab === 'daily-service' && <EquipmentDailyServiceTab unit={unit} />}
                            {activeTab === 'pm' && <SingleUnitPMTab unit={unit} />}
                            {activeTab === 'tires' && <TiresTab unit={unit} />}
                            {activeTab === 'cost-expense' && <SingleUnitCostTab unit={unit} />}
                        </>
                    )}
                </div>
            </Card>
            <FormBuilderOverlay
                visible={showInspectionFormOverlay}
                defaultFormName={pendingInspectionFormName}
                formType={FORM_TYPES.INSPECTION}
                onDismiss={() => {
                    setShowInspectionFormOverlay(false);
                    setPendingInspectionFormName('');
                }}
                onFormSaved={(form) => {
                    setEditInspectionFormId(form.id);
                    setPendingInspectionFormName('');
                }}
            />
            <FormBuilderOverlay
                visible={showDailyServiceFormOverlay}
                defaultFormName={pendingDailyServiceFormName}
                formType={FORM_TYPES.DAILY_SERVICE}
                onDismiss={() => {
                    setShowDailyServiceFormOverlay(false);
                    setPendingDailyServiceFormName('');
                }}
                onFormSaved={(form) => {
                    setFormData((prev: Partial<SingleUnit>) => ({ ...prev, dailyServiceLogFormId: form.id }));
                    setPendingDailyServiceFormName('');
                }}
            />

            {/* Unarchive Confirmation Modal */}
            {showUnarchiveModal && (
                <div className="confirm-modal-overlay" onClick={() => setShowUnarchiveModal(false)}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-modal-icon">📂</div>
                        <h3>Unarchive this unit?</h3>
                        <p>
                            This will reactivate <strong>{unit.name}</strong> for inspections, daily service, PM
                            scheduling, tire tracking, and cost tracking.
                        </p>
                        <div className="confirm-modal-actions">
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Cancel"
                                buttonOnClick={() => setShowUnarchiveModal(false)}
                            />
                            <Btn variant="primary" buttonText="Unarchive" buttonOnClick={handleUnarchive} />
                        </div>
                    </div>
                </div>
            )}

            <EquipmentUnsavedExitModal
                visible={showUnsavedExitModal}
                onDismiss={() => {
                    setShowUnsavedExitModal(false);
                    cancelPendingNavigation();
                    unsavedExitIntentRef.current = 'navigation';
                }}
                onConfirmLeave={() => {
                    setShowUnsavedExitModal(false);
                    if (unsavedExitIntentRef.current === 'closeEdit') {
                        unsavedExitIntentRef.current = 'navigation';
                        handleDiscard();
                        return;
                    }
                    confirmPendingNavigation();
                }}
            />
        </div>
    );
};
